import type {
  SnapshotMeta, SnapshotPayload, TrendPoint, PicRow, Settings, Country, SyncStatus,
} from "@shared/types.js";
import { readToken, clearToken } from "./lib/auth.js";

/* Told when the server stops accepting the token, so the app can put the
   login page back up instead of leaving a dashboard of stale numbers on
   screen. Set once by App; every call routes its 401 through here. */
let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/** Every request carries the token; the server rejects the ones that do not. */
function authed(init?: RequestInit): RequestInit {
  const token = readToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set("x-app-token", token);
  return { ...init, headers };
}

/* A 401 means the token is no longer good - the password changed, or the
   server restarted under a different one. Drop it and send the user back to
   the gate rather than surfacing "Not signed in" as a data error. */
function rejected(status: number): void {
  if (status !== 401) return;
  clearToken();
  onUnauthorized();
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, authed(init));
  if (!res.ok) {
    rejected(res.status);
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; detail?: string };
      detail = body.error ?? body.detail ?? detail;
    } catch { /* non-JSON error body */ }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

/* Every call is scoped to one country: the two Masterfiles are separate
   datasets and must never be added together in a statistic. */
const q = (country: Country, extra?: Record<string, string | number>) => {
  const u = new URLSearchParams({ country, ...(extra ?? {}) } as Record<string, string>);
  return u.toString();
};

export const api = {
  listSnapshots: (country: Country) =>
    req<SnapshotMeta[]>(`/api/snapshots?${q(country)}`),

  getSnapshot: (id: number | "latest", country: Country) =>
    req<SnapshotPayload>(`/api/snapshots/${id}?${q(country)}`),

  getTrend: (includeCatE: boolean, country: Country) =>
    req<TrendPoint[]>(`/api/trend?${q(country, { includeCatE: includeCatE ? 1 : 0 })}`),

  getPic: (id: number | "latest", includeCatE: boolean, country: Country) =>
    req<PicRow[]>(`/api/snapshots/${id}/pic?${q(country, { includeCatE: includeCatE ? 1 : 0 })}`),

  getRules: () => req<Record<string, { label: string; why: string }>>("/api/rules"),

  getSettings: (country: Country) => req<Settings>(`/api/settings?${q(country)}`),

  setSettings: (s: Settings, country: Country) =>
    req<Settings>(`/api/settings?${q(country)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(s),
    }),

  /* ------------------------------------------------------ sharepoint sync */

  getSyncStatus: (country: Country) => req<SyncStatus>(`/api/sync?${q(country)}`),

  /** Pull the workbook now. `force` re-imports even when it has not changed. */
  syncNow: (country: Country, force = false) =>
    req<{ changed: boolean; snapshotId: number | null; reason: string | null; status: SyncStatus }>(
      `/api/sync/${country}${force ? "?force=1" : ""}`,
      { method: "POST" },
    ),

  async upload(file: File, label: string, country: Country): Promise<{ meta: SnapshotMeta; notes: string[] }> {
    const fd = new FormData();
    if (label.trim()) fd.append("label", label.trim());
    fd.append("file", file, file.name);
    return req<{ meta: SnapshotMeta; notes: string[] }>(`/api/snapshots?${q(country)}`, {
      method: "POST", body: fd,
    });
  },

  async remove(id: number, country: Country): Promise<void> {
    const res = await fetch(`/api/snapshots/${id}?${q(country)}`, authed({ method: "DELETE" }));
    if (!res.ok && res.status !== 204) {
      rejected(res.status);
      throw new Error(`Delete failed (${res.status})`);
    }
  },

  rename: (id: number, label: string | null, country: Country) =>
    req<SnapshotMeta>(`/api/snapshots/${id}?${q(country)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ label }),
    }),
};
